import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, Plus, Store, Pause, Play, KeyRound, Eye, Trash2, Copy, Receipt, Loader2, Mail } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import BusinessInvoices from '../components/platform/BusinessInvoices';
import NewBusinessDialog from '../components/platform/NewBusinessDialog';
import ConvertTrialDialog from '../components/platform/ConvertTrialDialog';
import BusinessActivityDialog, { UsageBadge } from '../components/platform/BusinessActivity';
import TrialFunnel from '../components/platform/TrialFunnel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function PlatformAdmin() {
  const { token, getAuthHeader } = useAuth();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [invoiceBiz, setInvoiceBiz] = useState(null);
  const [convertBiz, setConvertBiz] = useState(null);
  const [activityBiz, setActivityBiz] = useState(null);
  const [busy, setBusy] = useState('');
  const [showAdTrials, setShowAdTrials] = useState(false);

  const load = useCallback(async () => {
    try {
      const [b, s] = await Promise.all([
        axios.get(`${API}/platform/businesses`, { headers: getAuthHeader() }),
        axios.get(`${API}/platform/summary`, { headers: getAuthHeader() }),
      ]);
      setRows(b.data);
      setSummary(s.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load businesses');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { if (token) load(); }, [token, load]);

  const act = async (key, fn, okMsg) => {
    setBusy(key);
    try { await fn(); if (okMsg) toast.success(okMsg); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
    finally { setBusy(''); }
  };

  const toggleStatus = (b) => act(`status-${b.id}`, () =>
    axios.put(`${API}/platform/businesses/${b.id}`, { status: b.status === 'active' ? 'suspended' : 'active' }, { headers: getAuthHeader() }),
    b.status === 'active' ? `${b.name} suspended — their logins are blocked` : `${b.name} reactivated`);

  const changeFee = (b) => {
    const v = window.prompt(`Monthly fee per store for ${b.name} (NZD, ex GST)`, b.price_per_store);
    if (v === null || v === '' || isNaN(Number(v))) return;
    act(`fee-${b.id}`, () => axios.put(`${API}/platform/businesses/${b.id}`, { price_per_store: Number(v) }, { headers: getAuthHeader() }), 'Fee updated');
  };

  const addStore = (b) => {
    const name = window.prompt(`New store name for ${b.name}`);
    if (!name) return;
    act(`store-${b.id}`, () => axios.post(`${API}/platform/businesses/${b.id}/stores`, { name }, { headers: getAuthHeader() }), `Store "${name}" added`);
  };

  const resetPassword = (b) => {
    const pw = window.prompt(`New password for ${b.owner_email} (min 6 chars)`);
    if (!pw) return;
    act(`pw-${b.id}`, () => axios.post(`${API}/platform/businesses/${b.id}/reset-password`, { password: pw }, { headers: getAuthHeader() }), 'Password reset');
  };

  const recopy = (b) => act(`copy-${b.id}`, async () => {
    const r = await axios.post(`${API}/platform/businesses/${b.id}/copy-catalogue`, {}, { headers: getAuthHeader() });
    toast.success(`Added ${r.data.products_copied} new products (${r.data.products_skipped} already there)`);
  });

  const impersonate = (b) => act(`imp-${b.id}`, async () => {
    const r = await axios.post(`${API}/platform/businesses/${b.id}/impersonate`, {}, { headers: getAuthHeader() });
    sessionStorage.setItem('adminReturnToken', token);
    localStorage.setItem('token', r.data.access_token);
    localStorage.removeItem('staffToken');
    localStorage.removeItem('activeStaff');
    localStorage.setItem('selectedStore', 'orewa');
    window.location.href = '/dashboard';
  });

  const sendInvite = (b) => {
    const note = window.prompt(`Email ${b.invite_count ? 'a fresh' : 'the'} welcome email to ${b.contact_email || b.owner_email}?\nThis sets a NEW temporary password and includes it in the email.\n\nOptional personal note:`, '');
    if (note === null) return;
    act(`inv-${b.id}`, async () => {
      const r = await axios.post(`${API}/platform/businesses/${b.id}/invite`, { login_url: 'https://www.supplytrack.co.nz/login', personal_note: note || null }, { headers: getAuthHeader() });
      toast.success(`Invite sent to ${r.data.sent_to} (you were BCC'd)`);
    });
  };

  const extendTrial = (b) => {
    const v = window.prompt(`Extend ${b.name}'s trial by how many days?`, 1);
    if (v === null || isNaN(Number(v)) || Number(v) < 1) return;
    act(`ext-${b.id}`, () => axios.post(`${API}/platform/businesses/${b.id}/extend-trial`, { days: Number(v) }, { headers: getAuthHeader() }), 'Trial extended');
  };

  const toggleProtocols = (b) => {
    let price = b.protocol_addon_price || 0;
    if (!b.protocol_addon) {
      const v = window.prompt(`Protocols add-on monthly price for ${b.name} (NZD)`, price || 50);
      if (v === null || isNaN(Number(v))) return;
      price = Number(v);
    }
    act(`proto-${b.id}`, () => axios.put(`${API}/platform/businesses/${b.id}`, { protocol_addon: !b.protocol_addon, protocol_addon_price: price }, { headers: getAuthHeader() }),
      b.protocol_addon ? 'Protocols add-on turned off' : `Protocols add-on on at ${money(price)}/mo`);
  };

  const remove = (b) => {
    if (window.prompt(`This permanently deletes ${b.name} and ALL their data. Type DELETE to confirm`) !== 'DELETE') return;
    act(`del-${b.id}`, () => axios.delete(`${API}/platform/businesses/${b.id}`, { headers: getAuthHeader() }), `${b.name} removed`);
  };

  const adTrials = rows.filter(r => !r.is_platform && r.public_trial);
  const customers = rows.filter(r => !r.is_platform && (!r.public_trial || showAdTrials));
  const mine = rows.find(r => r.is_platform);

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto" data-testid="platform-admin-page">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-semibold text-[#1B5E20]" style={{ fontFamily: 'Work Sans, sans-serif' }}>Admin Console</h1>
          <p className="text-sm text-gray-500 mt-1">Your SupplyTrack customers — each one is a separate business with its own login, staff, stores and prices.</p>
        </div>
        <div className="flex items-center gap-2">
          {mine && (
            <Button variant="outline" onClick={() => setActivityBiz(mine)} className="rounded-full" data-testid="my-login-activity-btn">
              <Eye className="w-4 h-4 mr-2" /> Who logged into my business
            </Button>
          )}
          <Button onClick={() => setShowNew(true)} className="bg-[#1B5E20] hover:bg-[#2E7D32] rounded-full" data-testid="new-business-btn">
            <Plus className="w-4 h-4 mr-2" /> New customer
          </Button>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="platform-summary">
          <Stat label="Customers" value={summary.customers} sub={`${summary.active} paying · ${summary.trials || 0} trial · ${summary.suspended} suspended`} />
          <Stat label="Monthly recurring (ex GST)" value={money(summary.mrr)} sub={`${money(summary.default_price_per_store)} + GST / store · ${money(summary.mrr_incl_gst)} incl GST`} />
          <Stat label="Outstanding invoices (incl GST)" value={money(summary.amount_due)} sub="bank transfer, marked paid by you" warn={summary.amount_due > 0} />
          <Stat label="Onboarding fee" value={`${money(summary.default_onboarding_fee)} + GST`} sub={`one-off · ${money(summary.default_onboarding_fee * (1 + (summary.gst_rate || 0.15)))} incl GST`} />
        </div>
      )}

      <TrialFunnel />

      {adTrials.length > 0 && (
        <button onClick={() => setShowAdTrials(s => !s)} className="text-xs text-[#1B5E20] underline underline-offset-4 mb-3" data-testid="toggle-ad-trials">
          {showAdTrials ? 'Hide' : 'Show'} the {adTrials.length} self-serve trial store{adTrials.length === 1 ? '' : 's'} from the trial page in the list below
        </button>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : customers.length === 0 ? (
        <div className="border border-dashed rounded-xl p-12 text-center text-gray-500" data-testid="no-customers">
          <Building2 className="w-8 h-8 mx-auto mb-3 text-gray-300" />
          No customers yet. Click <b>New customer</b> to set up the first store.
        </div>
      ) : (
        <div className="space-y-3">
          {customers.map(b => (
            <div key={b.id} className="bg-white border border-gray-200 rounded-xl p-4 md:p-5" data-testid={`business-card-${b.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-lg font-semibold text-gray-900" data-testid="business-card-name">{b.name}</h2>
                    <Badge className={b.status === 'active' ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100' : b.status === 'trial' ? (b.trial_expired ? 'bg-gray-200 text-gray-700 hover:bg-gray-200' : 'bg-sky-100 text-sky-800 hover:bg-sky-100') : 'bg-red-100 text-red-800 hover:bg-red-100'} data-testid="business-status">
                      {b.status === 'trial' ? (b.trial_expired ? 'trial ended' : b.trial_ends_at ? `trial · ends ${new Date(b.trial_ends_at).toLocaleString('en-NZ', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}` : `trial · ${b.trial_hours || 1}h from first sign-in`) : b.status}
                    </Badge>
                    {b.amount_due > 0 && <Badge variant="outline" className="border-amber-300 text-amber-800">{money(b.amount_due)} due</Badge>}
                  </div>
                  <p className="text-sm text-gray-500 mt-0.5">{b.owner_name} · {b.owner_email}</p>
                  {b.invite_sent_at && <p className="text-xs text-sky-700 mt-0.5" data-testid="invite-status">Invite emailed {new Date(b.invite_sent_at).toLocaleString('en-NZ', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })} to {b.invite_sent_to}{b.invite_count > 1 ? ` (×${b.invite_count})` : ''}</p>}
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {b.stores.map(s => (
                      <span key={s.code} className="inline-flex items-center gap-1 text-xs bg-gray-50 border border-gray-200 rounded-full px-2.5 py-1 text-gray-700">
                        <Store className="w-3 h-3 text-amber-500" /> {s.name}
                      </span>
                    ))}
                    {b.store_count < 2 && (
                      <button onClick={() => addStore(b)} className="text-xs text-[#1B5E20] underline px-1" data-testid="add-store-btn">+ add store</button>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  {b.is_trial && <p className="text-[11px] uppercase tracking-wider text-sky-700">Quote if they sign</p>}
                  <p className="text-2xl font-semibold text-gray-900" data-testid="business-monthly-fee">{money(b.monthly_fee)}<span className="text-sm text-gray-400 font-normal"> + GST /mo</span></p>
                  <p className="text-xs text-gray-500">{b.store_count} store{b.store_count === 1 ? '' : 's'} × {money(b.price_per_store)}{b.protocol_addon ? ` + protocols ${money(b.protocol_addon_price)}` : ''} · {money(b.monthly_fee_incl_gst)} incl GST</p>
                </div>
              </div>

              <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mt-4 text-xs text-gray-600 border-t pt-3">
                <Mini label="Products" value={b.products.toLocaleString()} />
                <Mini label="Staff" value={b.staff} />
                <Mini label="Sales 30d" value={b.sales_30d} />
                <Mini label="Revenue 30d" value={money(b.revenue_30d)} />
                <Mini label="Last sale" value={b.last_sale_at ? new Date(b.last_sale_at).toLocaleDateString('en-NZ') : '—'} />
                <Mini label="Customer since" value={new Date(b.created_at).toLocaleDateString('en-NZ')} />
              </div>

              <UsageBadge usage={b.usage} onOpen={() => setActivityBiz(b)} testId={`usage-${b.id}`} />

              <div className="flex flex-wrap gap-2 mt-4">
                {b.is_trial && (
                  <>
                    <Button size="sm" className="bg-sky-700 hover:bg-sky-800" onClick={() => setConvertBiz(b)} data-testid="convert-trial-btn">Convert to paying customer</Button>
                    <Button size="sm" variant="outline" onClick={() => extendTrial(b)} disabled={busy === `ext-${b.id}`} data-testid="extend-trial-btn">Extend trial</Button>
                  </>
                )}
                {!b.is_trial && <Button size="sm" variant="outline" onClick={() => setInvoiceBiz(b)} data-testid="invoices-btn"><Receipt className="w-3.5 h-3.5 mr-1" /> Invoices</Button>}
                <Button size="sm" variant="outline" onClick={() => changeFee(b)} disabled={busy === `fee-${b.id}`} data-testid="change-fee-btn">Change fee</Button>
                <Button size="sm" variant="outline" onClick={() => toggleProtocols(b)} disabled={busy === `proto-${b.id}`} data-testid="toggle-protocols-btn">
                  {b.protocol_addon ? `Protocols on (${money(b.protocol_addon_price)}/mo)` : 'Add Protocols'}
                </Button>
                {!b.is_trial && (
                  <Button size="sm" variant="outline" onClick={() => toggleStatus(b)} disabled={busy === `status-${b.id}`} data-testid="toggle-status-btn">
                    {b.status === 'active' ? <><Pause className="w-3.5 h-3.5 mr-1" /> Suspend</> : <><Play className="w-3.5 h-3.5 mr-1" /> Reactivate</>}
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => sendInvite(b)} disabled={busy === `inv-${b.id}`} data-testid="send-invite-btn"><Mail className="w-3.5 h-3.5 mr-1" /> {b.invite_sent_at ? 'Resend invite email' : 'Email invite'}</Button>
                <Button size="sm" variant="outline" onClick={() => resetPassword(b)} disabled={busy === `pw-${b.id}`} data-testid="reset-password-btn"><KeyRound className="w-3.5 h-3.5 mr-1" /> Reset password</Button>
                <Button size="sm" variant="outline" onClick={() => recopy(b)} disabled={busy === `copy-${b.id}`} data-testid="recopy-btn"><Copy className="w-3.5 h-3.5 mr-1" /> Add my new products</Button>
                <Button size="sm" variant="outline" onClick={() => impersonate(b)} disabled={busy === `imp-${b.id}`} data-testid="impersonate-btn"><Eye className="w-3.5 h-3.5 mr-1" /> Sign in as them</Button>
                <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 ml-auto" onClick={() => remove(b)} disabled={busy === `del-${b.id}`} data-testid="delete-business-btn"><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <NewBusinessDialog open={showNew} onClose={() => setShowNew(false)} onCreated={load} defaults={summary} />
      <ConvertTrialDialog business={convertBiz} onClose={() => setConvertBiz(null)} onConverted={load} />
      <BusinessActivityDialog business={activityBiz} onClose={() => setActivityBiz(null)} />

      <Dialog open={!!invoiceBiz} onOpenChange={(o) => !o && setInvoiceBiz(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Invoices — {invoiceBiz?.name}</DialogTitle></DialogHeader>
          {invoiceBiz && <BusinessInvoices business={invoiceBiz} onChange={load} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Stat = ({ label, value, sub, warn }) => (
  <div className={`rounded-xl border p-4 ${warn ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-white'}`}>
    <p className="text-[11px] uppercase tracking-wider text-gray-500">{label}</p>
    <p className="text-2xl font-semibold text-gray-900 mt-1">{value}</p>
    {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
  </div>
);

const Mini = ({ label, value }) => (
  <div><p className="text-gray-400">{label}</p><p className="font-medium text-gray-800">{value}</p></div>
);

export { Label, Input };
